/**
 * PaymentAllocationPage — pick a posted Payment Voucher (PYV) and apply it
 * across one or more open supplier bills (Tally-grade bill-wise allocation).
 *
 * Flow:
 *   1. Pick supplier
 *   2. Pick a PYV voucher (auto-filtered to that supplier)
 *   3. Show open bills for the supplier
 *   4. Operator types amount per bill — running total compared to voucher total
 *   5. Save → backend stores allocations, AR/bill-wise reports auto-net them
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, InputNumber, Row, Select, Space, Spin,
  Statistic, Table, Tag, Typography,
} from 'antd'
import {
  CheckCircleOutlined, LinkOutlined, UserOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Title, Text } = Typography
const NUM = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' as const }
const fmt = (v: string | number | null | undefined, dp = 2) =>
  v == null ? '—' : Number(v).toLocaleString('en-IN',
    { minimumFractionDigits: dp, maximumFractionDigits: dp })


interface SupplierLite { id: string; supplier_name: string; gstin: string | null }
interface VoucherLite {
  id: string; voucher_no: string; voucher_date: string
  total_amount: string; narration: string | null
}
interface Bill {
  pve_id: string; bill_ref_name: string; invoice_no: string
  invoice_date: string; due_date: string | null
  total_payable: string; dn_total: string; net_outstanding: string
}
interface AllocResponse {
  voucher_id: string; voucher_no: string
  voucher_total: string; allocated_total: string; unallocated_balance: string
  allocations: { pve_id: string; amount: number; allocation_type: string; notes: string | null }[]
}

export default function PaymentAllocationPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const { message: msg } = App.useApp()
  const qc = useQueryClient()

  const [supplierId, setSupplierId] = useState<string | null>(null)
  const [voucherId, setVoucherId] = useState<string | null>(null)
  // Per-bill allocation amounts: { pve_id → amount }
  const [allocMap, setAllocMap] = useState<Record<string, number>>({})

  // ── Data fetches ─────────────────────────────────────────────────────
  const { data: suppliers = [] } = useQuery<SupplierLite[]>({
    queryKey: ['suppliers-lite', plantId],
    queryFn: () => apiClient.get('/masters/suppliers',
      { params: { plant_id: plantId, page_size: 1000 } })
      .then(r => (r.data?.items ?? r.data ?? []).map((s: any) => ({
        id: s.id, supplier_name: s.supplier_name, gstin: s.gstin ?? null,
      }))),
    enabled: !!plantId,
  })

  // Fetch all PYV vouchers for the plant; filter client-side by supplier name
  // in narration. Backend doesn't have a direct vendor link on payment vouchers.
  const { data: pyvVouchers = [] } = useQuery<VoucherLite[]>({
    queryKey: ['pyv-vouchers', plantId],
    queryFn: () => apiClient.get('/accounting/vouchers',
      { params: { plant_id: plantId, voucher_type: 'PYV', limit: 200 } })
      .then(r => r.data ?? []),
    enabled: !!plantId,
  })

  const supplierName = useMemo(() =>
    suppliers.find(s => s.id === supplierId)?.supplier_name ?? '', [supplierId, suppliers])
  const supplierVouchers = useMemo(() =>
    !supplierName ? [] :
      pyvVouchers.filter(v =>
        (v.narration ?? '').toLowerCase().includes(supplierName.toLowerCase())),
    [pyvVouchers, supplierName])

  // Bill-wise outstanding for the selected supplier — drives the allocation table
  const { data: billWise } = useQuery<any>({
    queryKey: ['bill-wise', plantId, supplierId],
    queryFn: () => apiClient.get('/accounting/bill-wise-outstanding',
      { params: { plant_id: plantId, supplier_id: supplierId } }).then(r => r.data),
    enabled: !!plantId && !!supplierId,
  })

  const openBills: Bill[] = useMemo(() => {
    if (!billWise?.suppliers) return []
    return billWise.suppliers.flatMap((s: any) => s.bills ?? [])
  }, [billWise])

  // Existing allocations on the chosen voucher
  const { data: existingAllocs } = useQuery<AllocResponse>({
    queryKey: ['voucher-allocations', voucherId],
    queryFn: () => apiClient.get(`/accounting/payment-allocations/${voucherId}`)
      .then(r => r.data),
    enabled: !!voucherId,
  })

  // Pre-fill allocMap from existing allocations when voucher selected
  useEffect(() => {
    if (existingAllocs) {
      const m: Record<string, number> = {}
      existingAllocs.allocations.forEach(a => { m[a.pve_id] = a.amount })
      setAllocMap(m)
    } else {
      setAllocMap({})
    }
  }, [existingAllocs?.voucher_id])

  // Selected voucher details
  const selectedVoucher = pyvVouchers.find(v => v.id === voucherId)
  const voucherTotal = Number(selectedVoucher?.total_amount ?? 0)
  const allocatedTotal = Object.values(allocMap).reduce((s, n) => s + (n || 0), 0)
  const unallocated = voucherTotal - allocatedTotal

  // ── Save mutation ────────────────────────────────────────────────────
  const saveMut = useMutation({
    mutationFn: () => apiClient.post('/accounting/payment-allocations', {
      plant_id: plantId,
      voucher_id: voucherId,
      allocations: Object.entries(allocMap)
        .filter(([_, amt]) => amt > 0)
        .map(([pve_id, amount]) => ({
          pve_id,
          amount,
          allocation_type: 'AGAINST_REF',
        })),
    }),
    onSuccess: () => {
      msg.success(`Allocated ₹${fmt(allocatedTotal, 0)} across ${
        Object.values(allocMap).filter(v => v > 0).length} bill(s)`)
      qc.invalidateQueries({ queryKey: ['voucher-allocations'] })
      qc.invalidateQueries({ queryKey: ['bill-wise'] })
      qc.invalidateQueries({ queryKey: ['ar-aging'] })
      qc.invalidateQueries({ queryKey: ['cash-forecast'] })
    },
    onError: (e: any) => msg.error(e.response?.data?.detail || 'Save failed'),
  })

  // ── Auto-allocate FIFO (oldest bills first) ──────────────────────────
  const autoAllocate = () => {
    let remaining = voucherTotal
    const m: Record<string, number> = {}
    for (const b of openBills) {
      if (remaining <= 0) break
      const owe = Number(b.net_outstanding)
      const apply = Math.min(remaining, owe)
      if (apply > 0) {
        m[b.pve_id] = apply
        remaining -= apply
      }
    }
    setAllocMap(m)
    msg.info(`Auto-allocated FIFO: ${Object.keys(m).length} bill(s) covered`)
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3} style={{ marginBottom: 0 }}>
        <LinkOutlined style={{ marginRight: 8, color: '#1677ff' }} />
        Payment Allocation
      </Title>
      <Text type="secondary">
        Apply a posted Payment Voucher across one or more open supplier bills.
        Once saved, bill-wise outstanding + AR aging + cash forecast all reflect
        the allocation automatically.
      </Text>

      {/* Filters */}
      <Card style={{ marginTop: 16 }} styles={{ body: { padding: 16 } }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={6}>
            <Select value={plantId} onChange={setPlantId}
              style={{ width: '100%' }}
              options={plants.map((p: any) => ({ value: p.id, label: p.name ?? p.plant_name }))} />
          </Col>
          <Col xs={24} md={9}>
            <Select value={supplierId} onChange={(v) => { setSupplierId(v); setVoucherId(null) }}
              placeholder="1. Pick supplier" showSearch allowClear
              style={{ width: '100%' }}
              options={suppliers.map(s => ({ value: s.id,
                label: s.gstin ? `${s.supplier_name}  ·  ${s.gstin}` : s.supplier_name }))}
              filterOption={(input, opt) =>
                (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())} />
          </Col>
          <Col xs={24} md={9}>
            <Select value={voucherId} onChange={setVoucherId}
              placeholder="2. Pick payment voucher (PYV)" showSearch allowClear
              style={{ width: '100%' }}
              disabled={!supplierId}
              options={supplierVouchers.map(v => ({
                value: v.id,
                label: `${v.voucher_no} · ${dayjs(v.voucher_date).format('DD-MMM-YY')} · ₹${fmt(v.total_amount, 0)}`,
              }))} />
          </Col>
        </Row>
      </Card>

      {!supplierId && (
        <Alert type="info" showIcon style={{ marginTop: 16 }}
          message="Pick a supplier and payment voucher to start allocating." />
      )}

      {voucherId && selectedVoucher && (
        <>
          {/* Voucher + allocation summary */}
          <Row gutter={[12, 12]} style={{ marginTop: 16 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Voucher Total"
                  value={voucherTotal} precision={2}
                  prefix="₹" valueStyle={{ ...NUM }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Allocated"
                  value={allocatedTotal} precision={2}
                  prefix="₹"
                  valueStyle={{ ...NUM, color: '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small"
                style={{ borderColor: Math.abs(unallocated) > 0.01 ? '#ffa39e' : '#b7eb8f',
                         background: Math.abs(unallocated) > 0.01 ? '#fff1f0' : '#f6ffed' }}>
                <Statistic title="Unallocated"
                  value={unallocated} precision={2}
                  prefix="₹"
                  valueStyle={{ ...NUM,
                    color: Math.abs(unallocated) > 0.01 ? '#cf1322' : '#52c41a' }} />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Button block onClick={autoAllocate}>FIFO auto-allocate</Button>
                  <Button block type="primary"
                    icon={<CheckCircleOutlined />}
                    loading={saveMut.isPending}
                    disabled={Object.keys(allocMap).filter(k => allocMap[k] > 0).length === 0
                      || allocatedTotal > voucherTotal + 0.01}
                    onClick={() => saveMut.mutate()}>
                    Save allocation
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Per-bill table */}
          <Card style={{ marginTop: 16 }}
            title={<Space><UserOutlined />{supplierName} · {openBills.length} open bill(s)</Space>}>
            {openBills.length === 0 && (
              <Alert type="success" showIcon
                message="No open bills for this supplier — nothing to allocate." />
            )}
            {openBills.length > 0 && (
              <Table size="small" dataSource={openBills} rowKey="pve_id"
                pagination={false}
                scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Bill', dataIndex: 'bill_ref_name',
                    render: (v: string) => <Text strong style={{ fontFamily: 'monospace', fontSize: 11 }}>{v}</Text> },
                  { title: 'Inv Date', dataIndex: 'invoice_date',
                    render: (v: string) => dayjs(v).format('DD-MMM-YY') },
                  { title: 'Due', dataIndex: 'due_date',
                    render: (v: string | null) => v ? dayjs(v).format('DD-MMM-YY') : '—' },
                  { title: 'Gross ₹', dataIndex: 'total_payable', align: 'right' as const,
                    render: (v: string) => <span style={NUM}>₹{fmt(v, 0)}</span> },
                  { title: '− DN', dataIndex: 'dn_total', align: 'right' as const,
                    render: (v: string) => Number(v) > 0
                      ? <Text style={{ ...NUM, color: '#cf1322' }}>−₹{fmt(v, 0)}</Text> : '—' },
                  { title: 'Net Outstanding', dataIndex: 'net_outstanding', align: 'right' as const,
                    render: (v: string) => <Text strong style={{ ...NUM, color: '#52c41a' }}>
                      ₹{fmt(v, 0)}</Text> },
                  { title: 'Allocate ₹', key: 'alloc', align: 'right' as const, width: 160,
                    render: (_, r: Bill) => (
                      <InputNumber
                        size="small"
                        min={0}
                        max={Number(r.net_outstanding)}
                        value={allocMap[r.pve_id] ?? 0}
                        onChange={(v) => setAllocMap(prev => ({
                          ...prev, [r.pve_id]: Number(v) || 0,
                        }))}
                        precision={2}
                        style={{ width: '100%' }}
                        formatter={v => `₹ ${v}`}
                      />
                    ) },
                ]}
              />
            )}
          </Card>
        </>
      )}
    </div>
  )
}
